import { memo, useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { ModalBody, ModalContent, Modal, ModalHeader, ModalFooter } from '@heroui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useCreateModuleMutation } from '@/entities/Courses';
import { useGetAcademicStaffAllQuery } from '@/entities/AcademicStaff';
import { AppButton } from '@/shared/ui/AppButton';
import { CustomInput, CustomSelect, CustomTextarea } from '@/shared/ui/Fields';
import { scaleInOut } from '@/shared/lib/utils/modalTransitions';
import { useToast } from '@/shared/lib/hooks/useToast';
import styles from './AddModuleModal.module.scss';

interface AddModuleModalProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  courseId: number;
  intakeTermId: number;
  groupNumber: number;
  onSuccess?: () => void;
}

interface ModuleFormData {
  title: string;
  description: string;
  moduleTeacherId: string;
  lecturerId: string;
}

const moduleSchema = yup.object({
  title: yup.string().required('Module title is required').min(1).max(255),
  description: yup.string().required('Module description is required').min(1),
  moduleTeacherId: yup.string().optional(),
  lecturerId: yup.string().optional(),
}) as yup.ObjectSchema<ModuleFormData>;

export const AddModuleModal = memo(
  ({
    isOpen,
    onOpenChange,
    courseId,
    intakeTermId,
    groupNumber,
    onSuccess,
  }: AddModuleModalProps) => {
    const { toast } = useToast();
    const [createModule, { isLoading }] = useCreateModuleMutation();

    const { data: academicStaffData } = useGetAcademicStaffAllQuery(undefined, { skip: !isOpen });

    const teacherItems = useMemo(
      () =>
        (academicStaffData?.data || [])
          .filter((staff) => !staff.blocked)
          .map((staff) => ({
            key: String(staff.id),
            label: `${staff.firstName} ${staff.lastName}`,
          })),
      [academicStaffData?.data],
    );

    const {
      control,
      handleSubmit,
      formState: { errors },
      reset,
      watch,
    } = useForm<ModuleFormData>({
      resolver: yupResolver(moduleSchema) as any,
      defaultValues: {
        title: '',
        description: '',
        moduleTeacherId: '',
        lecturerId: '',
      },
    });

    const watchedTitle = watch('title');
    const watchedDescription = watch('description');
    const canSubmit = !!watchedTitle && !!watchedDescription;

    const onSubmit = async (data: ModuleFormData) => {
      try {
        const result = await createModule({
          intakeTermId,
          groupNumber,
          module: {
            title: data.title,
            description: data.description,
            moduleTeacherId: data.moduleTeacherId ? Number(data.moduleTeacherId) : undefined,
            lecturerId: data.lecturerId ? Number(data.lecturerId) : undefined,
          },
        }).unwrap();

        toast({
          title: 'Success',
          description: result.message || 'Module created successfully',
          color: 'success',
        });

        handleClose();
        onSuccess?.();
      } catch (error: any) {
        toast({
          title: 'Error',
          description: error?.data?.message || 'Failed to create module',
          color: 'danger',
        });
      }
    };

    const handleClose = () => {
      reset({
        title: '',
        description: '',
        moduleTeacherId: '',
        lecturerId: '',
      });
      onOpenChange(false);
    };

    return (
      <Modal
        isOpen={isOpen}
        onOpenChange={handleClose}
        placement="center"
        motionProps={scaleInOut}
        backdrop="opaque"
        scrollBehavior="inside"
        size="5xl"
        classNames={{
          base: 'p-9 max-w-[740px]',
          closeButton: styles.closeBtn,
          wrapper: styles.modalContent,
        }}
      >
        <ModalContent>
          {() => (
            <form
              onSubmit={handleSubmit(onSubmit as any)}
              className="flex flex-col max-h-[80vh] overflow-hidden"
            >
              <ModalHeader className={styles.title}>Add new module</ModalHeader>
              <ModalBody className="p-0 flex flex-col gap-6 flex-1 min-h-0 overflow-y-auto">
                <Controller
                  name="title"
                  control={control}
                  render={({ field }) => (
                    <CustomInput
                      {...field}
                      label="Module Title"
                      labelPlacement="outside"
                      placeholder="Enter module title"
                      errorMessage={errors.title?.message}
                      showError={!!errors.title}
                      isRequired
                    />
                  )}
                />

                <Controller
                  name="description"
                  control={control}
                  render={({ field }) => (
                    <CustomTextarea
                      {...field}
                      label="Module Description"
                      labelPlacement="outside"
                      placeholder="Enter module description"
                      errorMessage={errors.description?.message}
                      showError={!!errors.description}
                      isRequired
                    />
                  )}
                />

                <Controller
                  name="moduleTeacherId"
                  control={control}
                  render={({ field }) => (
                    <CustomSelect
                      style="black"
                      label="Module Lead"
                      labelPlacement="outside"
                      placeholder="Select the module lead"
                      items={teacherItems}
                      selectedKeys={field.value ? new Set([field.value]) : new Set()}
                      onSelectionChange={(keys) => {
                        const selected = Array.from(keys)[0] as string;
                        field.onChange(selected || '');
                      }}
                      errorMessage={errors.moduleTeacherId?.message}
                      showError={!!errors.moduleTeacherId}
                      popoverProps={{
                        placement: 'bottom',
                        shouldBlockScroll: false,
                        portalContainer:
                          typeof document !== 'undefined' ? document.body : undefined,
                      }}
                    />
                  )}
                />

                <Controller
                  name="lecturerId"
                  control={control}
                  render={({ field }) => (
                    <CustomSelect
                      style="black"
                      label="Lecturer"
                      labelPlacement="outside"
                      placeholder="Select lecturer"
                      items={teacherItems}
                      selectedKeys={field.value ? new Set([field.value]) : new Set()}
                      onSelectionChange={(keys) => {
                        const selected = Array.from(keys)[0] as string;
                        field.onChange(selected || '');
                      }}
                      errorMessage={errors.lecturerId?.message}
                      showError={!!errors.lecturerId}
                      popoverProps={{
                        placement: 'bottom',
                        shouldBlockScroll: false,
                        portalContainer:
                          typeof document !== 'undefined' ? document.body : undefined,
                      }}
                    />
                  )}
                />
              </ModalBody>
              <ModalFooter className="gap-4 pt-6 px-0 shrink-0">
                <AppButton fullWidth color="white" onPress={handleClose} type="button">
                  Cancel
                </AppButton>
                <AppButton
                  fullWidth
                  color="black"
                  type="submit"
                  isLoading={isLoading}
                  disabled={!canSubmit}
                  onClick={(e) => {
                    e.preventDefault();
                    handleSubmit(onSubmit as any)();
                  }}
                >
                  Create module
                </AppButton>
              </ModalFooter>
            </form>
          )}
        </ModalContent>
      </Modal>
    );
  },
);

AddModuleModal.displayName = 'AddModuleModal';
